import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';

import { SubmissionNotice } from '../../components/shared/SubmissionNotice';
import {
  BudgetRequestFormFields,
  BudgetRequestFormValues,
  BudgetRequestFormErrors,
} from '../../components/budget/BudgetRequestFormFields';
import { useCreateBudgetRequest } from '../../hooks/useBudgetMutations';
import { useBrgList } from '../../hooks/useBrgList';
import { ApiError } from '../../types';

/**
 * Initial empty form state for budget request
 */
const emptyForm: BudgetRequestFormValues = {
  brgId: '',
  submittedBy: '',
  requestAmount: '',
  projectName: '',
  scheduledDate: '',
  purpose: '',
  description: '',
};

/**
 * Budget Request Form Page
 *
 * Allows BRG Leads and Admins to create new budget requests.
 * Displays a submission notice, form fields, and action buttons.
 * Form requires validation before submission and assigns Pending status.
 */
export function BudgetRequestPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const createMutation = useCreateBudgetRequest();
  const { data: brgs = [], isLoading: isLoadingBrgs } = useBrgList();

  const [values, setValues] = useState<BudgetRequestFormValues>(emptyForm);
  const [errors, setErrors] = useState<BudgetRequestFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Auto-populate submittedBy field with current user's name
  useEffect(() => {
    const currentUserName = auth.name || 'Unknown User';
    setValues((prev) => ({
      ...prev,
      submittedBy: currentUserName,
    }));
  }, [auth.name]);

  /**
   * Handle field changes with dynamic validation clearing
   */
  function handleChange(field: keyof BudgetRequestFormValues, value: string) {
    setValues((prev) => ({ ...prev, [field]: value }));

    // Clear error for this field on change (only for fields in BudgetRequestFormErrors)
    const errorFields: (keyof BudgetRequestFormErrors)[] = ['brgId', 'submittedBy', 'requestAmount', 'purpose', 'description'];
    if (errorFields.includes(field as keyof BudgetRequestFormErrors) && errors[field as keyof BudgetRequestFormErrors]) {
      setErrors((prev) => ({ ...prev, [field as keyof BudgetRequestFormErrors]: undefined }));
    }
    setSubmitError(null);
  }

  /**
   * Check if all required fields are filled
   */
  function areRequiredFieldsFilled(): boolean {
    return (
      values.brgId.trim() !== '' &&
      values.submittedBy.trim() !== '' &&
      values.requestAmount.trim() !== '' &&
      parseFloat(values.requestAmount) > 0 &&
      parseFloat(values.requestAmount) <= 100000 &&
      values.purpose.trim() !== '' &&
      values.description.trim() !== ''
    );
  }

  /**
   * Validate all required fields
   */
  function validate(): BudgetRequestFormErrors {
    const errs: BudgetRequestFormErrors = {};

    if (!values.brgId.trim()) {
      errs.brgId = 'BRG name is required';
    }
    if (!values.submittedBy.trim()) {
      errs.submittedBy = 'Submitted by is required';
    }
    if (!values.requestAmount.trim() || parseFloat(values.requestAmount) <= 0) {
      errs.requestAmount = 'Request amount is required';
    } else if (parseFloat(values.requestAmount) > 100000) {
      errs.requestAmount = 'Maximum budget request is $100,000.00 per BRG';
    }
    if (!values.purpose.trim()) {
      errs.purpose = 'Purpose / description is required';
    }
    if (!values.description.trim()) {
      errs.description = 'Request description is required';
    }

    return errs;
  }

  /**
   * Handle form submission
   */
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      // Scroll to first error
      const firstErrorField = Object.keys(errs)[0];
      document.getElementById(firstErrorField)?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    setErrors({});
    setSubmitError(null);

    const parsedBrgId = parseInt(values.brgId, 10);
    // console.log('Form submission - values.brgId:', values.brgId, 'parsedBrgId:', parsedBrgId);
    
    if (isNaN(parsedBrgId) || !values.brgId) {
      setSubmitError('Please select a BRG and try again.');
      return;
    }

    try {
      // console.log('Submitting budget request with brgId:', parsedBrgId);
      await createMutation.mutateAsync({
        brgId: parsedBrgId,
        fiscalYear: `FY ${new Date().getFullYear()}`,
        projectEvent: values.projectName || undefined,
        purposeDescription: values.purpose,
        requestDescription: values.description,
        requestAmount: parseFloat(values.requestAmount),
        scheduledDate: values.scheduledDate || undefined,
      });

      // Show success message
      setSuccessMessage('Budget request was successfully submitted.');
      
      // Scroll to top so user sees the success message
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Reset form to empty state
      setValues(emptyForm);
      setErrors({});
      setSubmitError(null);
      
      // Auto-populate submittedBy again with current user name
      const currentUserName = auth.name || 'Unknown User';
      setValues((prev) => ({
        ...prev,
        submittedBy: currentUserName,
      }));

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setSubmitError('Invalid request data. Please check all fields and try again.');
      } else if (err instanceof ApiError && err.status === 404) {
        setSubmitError('The selected BRG was not found. Please refresh and try again.');
      } else if (err instanceof Error) {
        setSubmitError(err.message || 'Something went wrong. Please try again.');
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    }
  }

  /**
   * Handle form cancellation
   */
  function handleCancel() {
    void navigate(-1);
  }

  return (
    <div style={{ width: '100%', padding: '24px 80px', maxWidth: 'none' }}>
      {/*<PageHeader title="Budget Request" />*/}

      {/* Success Message Banner - At the very top */}
      {successMessage && (
        <div
          role="alert"
          style={{
            color: '#2e7d32',
            fontSize: '0.9rem',
            marginBottom: '16px',
            padding: '10px 14px',
            backgroundColor: '#f1f8e9',
            border: '1px solid #c5e1a5',
            borderRadius: '4px',
          }}
        >
          ✓ {successMessage}
        </div>
      )}

      <form onSubmit={(e) => void handleSubmit(e)} noValidate>
        {/* Form Container */}
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
            padding: '28px 32px',
            marginBottom: '20px',
          }}
        >
          {/* Submission Notice Banner */}
          <SubmissionNotice
            message="Budget requests are reviewed by the BRG Admin. You will receive an email notification once your request has been approved or denied."
          />

          {/* Form Fields */}
          <BudgetRequestFormFields
            values={values}
            errors={errors}
            brgs={brgs}
            onChange={handleChange}
            disabled={createMutation.isPending}
            isLoadingBrgs={isLoadingBrgs}
          />
        </div>

        {/* Submit Error Alert */}
        {submitError && (
          <div
            role="alert"
            style={{
              color: '#d32f2f',
              fontSize: '0.9rem',
              marginBottom: '16px',
              padding: '10px 14px',
              backgroundColor: '#fdf3f3',
              border: '1px solid #f5c2c2',
              borderRadius: '4px',
            }}
          >
            {submitError}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleCancel}
            disabled={createMutation.isPending}
            style={{
              padding: '9px 24px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: '#fff',
              color: '#333',
              cursor: createMutation.isPending ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: 500,
              opacity: createMutation.isPending ? 0.6 : 1,
              transition: 'all 0.2s ease',
            }}
          >
            CANCEL
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending || !areRequiredFieldsFilled()}
            style={{
              padding: '9px 24px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: (createMutation.isPending || !areRequiredFieldsFilled()) ? '#cccccc' : '#0078d4',
              color: '#fff',
              cursor: (createMutation.isPending || !areRequiredFieldsFilled()) ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: 500,
              transition: 'background-color 0.2s ease',
            }}
          >
            {createMutation.isPending ? 'SUBMITTING...' : 'SUBMIT'}
          </button>
        </div>
      </form>
    </div>
  );
}
